import { Flag, TriangleAlert } from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import {
  type FlagResolution,
  type IntakeFlag,
  useLeadIntake,
  useResolveFlag,
} from "@/lib/queries-intake";
import { relativeTime } from "@/lib/utils";

/**
 * The Review card beside a contact record: the supervisor flags an intake rule raised on this
 * lead, and the decision each one is waiting for (ACL-341).
 *
 * **A flag asks for a person; it never rejects anybody.** The lead keeps its stage, stays on its
 * lists and stays dialable while a flag is open — the card is the ask, not the verdict, and the
 * only thing that moves a lead is a recorded disposition (ADR-046).
 *
 * **The reason belongs to the audience the admin named.** The API sends `reason: null` to anyone
 * below the flag's `visible_to`, so a rep is told the lead is flagged and not why: the sentence
 * is written for a reviewer and may word the problem in terms nobody should read off a screen
 * mid-call. This card renders what it was given and never infers the missing text.
 *
 * The card renders nothing at all when the lead has no open flag, so a record that was never
 * flagged looks exactly as it did before any of this existed.
 */
export function ContactReviewFlags({ leadId }: { leadId: string }) {
  const { data } = useLeadIntake(leadId);
  const flags = data?.flags ?? [];
  if (flags.length === 0) return null;
  return (
    <Card>
      <CardHeader
        title="Review"
        description={
          flags.length === 1
            ? "An intake rule asked for a second pair of eyes."
            : `${flags.length} intake rules asked for a second pair of eyes.`
        }
        action={
          <Badge tone="warning" className="gap-1">
            <Flag className="size-3" />
            {flags.length === 1 ? "Flagged" : `${flags.length} flags`}
          </Badge>
        }
      />
      <CardBody className="space-y-3">
        {flags.map((flag) => (
          <FlagRow key={flag.id} flag={flag} leadId={leadId} />
        ))}
      </CardBody>
    </Card>
  );
}

/**
 * What closing this flag means. A stale flag closes as `answer_changed` — the engine's own word
 * for a rule that stopped firing — so a reviewer agreeing it no longer applies is recorded as
 * that, not as a judgement they did not make.
 */
export function clearResolution(flag: Pick<IntakeFlag, "stale">): FlagResolution {
  return flag.stale ? "answer_changed" : "cleared";
}

/** The sentence, or the stand-in for a viewer the admin did not write it for. */
export function reasonText(flag: Pick<IntakeFlag, "reason" | "may_read_reason">): string {
  return flag.may_read_reason && flag.reason ? flag.reason : "Flagged for review";
}

/** One flag: why it was raised (when this viewer may read that), and the two ways to close it. */
function FlagRow({ flag, leadId }: { flag: IntakeFlag; leadId: string }) {
  const resolve = useResolveFlag(leadId);
  // The API decides this for itself; the button is hidden to match, so nobody is offered an
  // action that would come back a 409.
  const mayClose = flag.may_read_reason;

  return (
    <div className="rounded-md border border-warning-4 bg-warning-2 p-3">
      <div className="flex items-start gap-2">
        <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning-11" />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm font-medium text-text">
            {reasonText(flag)}
          </p>
          {!flag.may_read_reason && (
            <p className="text-xs text-text-muted">
              A {flag.visible_to} can see why.
            </p>
          )}
          <p className="text-xs text-text-muted">
            {flag.raised_at ? `Raised ${relativeTime(flag.raised_at)}` : "Raised"}
            {flag.stale && " · the rule no longer applies"}
          </p>
        </div>
      </div>
      {flag.stale && (
        // Said plainly rather than closed automatically: a rep changing an answer must never
        // clear a supervisor's flag on its own.
        <p className="mt-2 text-xs text-text-muted">
          An answer changed since this was raised. It stays open until someone closes it.
        </p>
      )}
      {mayClose && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={resolve.isPending}
            onClick={() =>
              resolve.mutate({ flagId: flag.id, resolution: clearResolution(flag) })
            }
          >
            {flag.stale ? "Close, it no longer applies" : "Looks fine"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={resolve.isPending}
            onClick={() => resolve.mutate({ flagId: flag.id, resolution: "rejected" })}
          >
            Not qualified
          </Button>
        </div>
      )}
      {resolve.isError && (
        <p className="mt-2 text-xs text-danger-11">That flag could not be closed.</p>
      )}
    </div>
  );
}
